"use client";

import React from "react";
import Link from "next/link";
import { Header } from "@/modules/shared/components/Header";
import { Footer } from "@/modules/shared/components/Footer";
import { PageTransition, FadeIn } from "@/modules/shared/components/PageTransition";

const EPISODES = [
  {
    id: 1,
    num: "01",
    title: "El Despertar en Jonia: Tales, Anaximandro y Anaxímenes",
    description: "El paso fundamental del mito al logos. Exploramos cómo los primeros pensadores en la costa jónica intentaron explicar el orden del cosmos sin recurrir a la mitología poética, inaugurando la búsqueda del principio originario (Arjé).",
    duration: "~45 min",
    status: "En Grabación",
    tags: ["Presocráticos", "Escuela de Mileto", "Arjé", "Cosmología"],
  },
  {
    id: 2,
    num: "02",
    title: "El Enigma del Cambio: Heráclito y Parménides",
    description: "La tensión fundacional de la metafísica occidental. El fuego en constante devenir y la dialéctica de Heráclito frente al ser inmóvil, eterno e indivisible de Parménides de Elea.",
    duration: "~52 min",
    status: "En Guion",
    tags: ["Metafísica", "Ontología", "Devenir", "Ser"],
  },
  {
    id: 3,
    num: "03",
    title: "Los Pluralistas y el Átomo: Empédocles, Anaxágoras y Demócrito",
    description: "La reconciliación entre la pluralidad del mundo físico y la exigencia de inmutabilidad ontológica. Las cuatro raíces, el nous ordenador y el nacimiento del atomismo clásico.",
    duration: "~48 min",
    status: "En Preparación",
    tags: ["Pluralismo", "Atomismo", "Nous", "Materialismo"],
  },
  {
    id: 4,
    num: "04",
    title: "El Giro Antropológico: Los Sofistas y el Enigma de Sócrates",
    description: "Atenas se convierte en el epicentro del debate humano. El relativismo de Protágoras, el arte de la persuasión y la figura revolucionaria de Sócrates que redefinió para siempre la ética y el examen interior.",
    duration: "~55 min",
    status: "En Preparación",
    tags: ["Sócrates", "Sofística", "Ética", "Mayéutica"],
  }
];

export default function PodcastClient() {
  return (
    <PageTransition>
      <div className="min-h-screen bg-bg-main text-text-pure font-sans selection:bg-gold/30 selection:text-gold-light relative">
        <Header />

        <main className="pt-24 md:pt-32 pb-24 relative z-10">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            
            {/* Podcast Hero Section */}
            <FadeIn>
              <div className="flex flex-col lg:flex-row items-center lg:items-start gap-12 lg:gap-16 mb-20 relative">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(197,160,89,0.08),transparent_60%)] pointer-events-none -z-10" />
                
                {/* Cover Art */}
                <div className="w-64 h-64 md:w-80 md:h-80 lg:w-96 lg:h-96 shrink-0 relative rounded-3xl overflow-hidden border border-gold/30 shadow-[0_0_50px_rgba(0,0,0,0.8)] group">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#121829] via-[#080c14] to-black z-0" />
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(197,160,89,0.15),transparent_70%)] z-10" />
                  
                  {/* Decorative Classical Borders */}
                  <div className="absolute inset-4 md:inset-5 border border-gold/25 z-20 pointer-events-none rounded-2xl" />
                  <div className="absolute inset-6 md:inset-7 border border-gold/10 z-20 pointer-events-none rounded-xl" />
                  
                  <div className="absolute top-8 left-0 right-0 flex justify-center z-20">
                    <span className="text-gold tracking-[0.4em] text-[9px] uppercase font-bold bg-bg-main/90 backdrop-blur-md px-4 py-1 rounded-full border border-gold/30 shadow-md">
                      Copleston Audio
                    </span>
                  </div>
                  
                  <div className="absolute inset-0 flex flex-col items-center justify-center z-30 p-8 text-center mt-6">
                    <h1 className="font-serif text-4xl md:text-6xl text-white font-light tracking-wider leading-none mb-3 drop-shadow-2xl">
                      LOGOS
                    </h1>
                    <div className="flex items-center gap-2 mb-4 w-3/4 max-w-[180px]">
                      <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent to-gold" />
                      <div className="w-1.5 h-1.5 rotate-45 bg-gold shadow-[0_0_8px_rgba(197,160,89,0.8)]" />
                      <div className="h-[1px] flex-1 bg-gradient-to-l from-transparent to-gold" />
                    </div>
                    <span className="font-sans text-[10px] md:text-xs text-text-muted tracking-[0.3em] uppercase mb-0.5">Historia de la</span>
                    <span className="font-sans text-xs md:text-sm text-gold tracking-[0.3em] uppercase font-bold drop-shadow-sm">Filosofía</span>
                  </div>

                  <div className="absolute bottom-6 left-0 right-0 flex justify-center z-20">
                    <span className="text-[9px] font-mono tracking-widest text-gold/80 uppercase bg-black/80 px-3 py-1 rounded-full border border-gold/20">
                      Temporada 1 · Próximamente
                    </span>
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1 text-center lg:text-left pt-4">
                  <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full border border-gold/30 bg-gold/10 mb-6 backdrop-blur-sm shadow-[0_0_20px_rgba(197,160,89,0.15)]">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-gold"></span>
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.25em] text-gold font-bold">
                      Temporada 1 — En Grabación · Próximo Estreno
                    </span>
                  </div>

                  <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white font-light tracking-wide mb-6 leading-tight">
                    El Viaje del <br className="hidden lg:block"/>
                    <span className="italic text-gold font-playfair">Pensamiento</span>
                  </h2>
                  
                  <p className="text-text-muted/90 font-light text-base md:text-lg leading-relaxed mb-8 max-w-2xl mx-auto lg:mx-0">
                    Únete a <span className="text-white font-medium">Edu Huallparimachi Huahuaccapa</span> en una travesía rigurosa y amena por la historia del pensamiento humano. Desde el amanecer de la razón en Jonia hasta los dilemas contemporáneos.
                  </p>

                  <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
                    <Link
                      href="/contacto?tema=circulo"
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-gold text-bg-main font-bold uppercase tracking-[0.2em] px-8 py-4 rounded-xl hover:bg-gold-light transition-all shadow-[0_0_25px_rgba(197,160,89,0.3)] hover:-translate-y-0.5 text-xs"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                      Notificarme del Lanzamiento
                    </Link>

                    <div className="flex items-center gap-4 px-6 py-3.5 border border-gold/20 rounded-xl bg-bg-secondary/40 backdrop-blur-sm">
                      <span className="text-[10px] text-text-muted tracking-[0.2em] uppercase font-bold">Plataformas:</span>
                      <span className="text-xs text-text-muted flex items-center gap-2">
                        <span>Spotify</span>
                        <span className="text-gold/40">•</span>
                        <span>Apple</span>
                        <span className="text-gold/40">•</span>
                        <span>YouTube</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </FadeIn>

            {/* Host Section */}
            <FadeIn delay={0.2}>
              <div className="bg-gradient-to-br from-[#0c1222] via-[#090d18] to-black border border-gold/20 rounded-3xl p-8 md:p-12 mb-20 shadow-2xl flex flex-col md:flex-row items-center gap-10 relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(197,160,89,0.08),transparent_60%)] pointer-events-none" />
                <div className="absolute top-0 right-0 w-32 h-32 bg-gold/5 rounded-bl-full pointer-events-none" />
                
                <div className="w-28 h-28 md:w-36 md:h-36 shrink-0 rounded-2xl bg-gradient-to-br from-gold/20 to-gold/5 border border-gold/40 flex items-center justify-center p-2 relative shadow-[0_0_30px_rgba(197,160,89,0.2)]">
                  <div className="w-full h-full rounded-xl bg-black/60 flex flex-col items-center justify-center border border-gold/20 text-center p-3">
                    <span className="font-serif text-2xl text-gold font-light">EH</span>
                    <span className="text-[8px] font-mono tracking-widest text-gold/70 uppercase mt-1">Fundador</span>
                  </div>
                </div>

                <div className="text-center md:text-left relative z-10 flex-1">
                  <span className="text-gold text-[10px] uppercase tracking-[0.3em] font-bold block mb-1">Director del Instituto Copleston</span>
                  <h3 className="font-serif text-2xl md:text-3xl text-white mb-3 font-light tracking-wide">
                    Edu Huallparimachi Huahuaccapa
                  </h3>
                  <p className="text-text-muted/90 font-light leading-relaxed text-sm md:text-base">
                    Investigador apasionado por la historia de las ideas, la filosofía clásica y el análisis estratégico. Su vocación es democratizar el pensamiento riguroso, transformando las obras cumbre de la filosofía universal en reflexiones claras, profundas y aplicables a la vida contemporánea.
                  </p>
                </div>
              </div>
            </FadeIn>

            {/* Episodes List (Syllabus) */}
            <FadeIn delay={0.3}>
              <div className="flex items-center justify-between gap-6 mb-10 border-b border-gold/15 pb-6">
                <div>
                  <h2 className="text-2xl md:text-3xl font-serif text-white uppercase tracking-wider font-light">
                    Syllabus de la <span className="text-gold font-playfair italic">Temporada 1</span>
                  </h2>
                  <p className="text-xs text-text-muted font-light mt-1">
                    Estructura temática de los episodios en fase de producción y montaje.
                  </p>
                </div>
                <span className="text-[10px] font-mono tracking-widest text-gold uppercase bg-gold/10 px-3 py-1 rounded-full border border-gold/30">
                  4 Episodios Planificados
                </span>
              </div>

              <div className="space-y-6">
                {EPISODES.map((ep) => (
                  <div 
                    key={ep.id}
                    className="group p-6 md:p-8 rounded-3xl border border-gold/15 bg-bg-secondary/30 backdrop-blur-sm hover:border-gold/40 hover:bg-bg-secondary/50 hover:shadow-[0_0_30px_rgba(197,160,89,0.1)] transition-all duration-500 relative overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-gold/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                    
                    <div className="flex flex-col md:flex-row gap-6 items-start md:items-center relative z-10">
                      {/* Episode Badge Icon */}
                      <div className="w-14 h-14 md:w-16 md:h-16 shrink-0 rounded-2xl bg-bg-main border border-gold/30 flex flex-col items-center justify-center text-gold group-hover:border-gold group-hover:bg-gold/10 transition-all duration-300 shadow-md">
                        <span className="text-[9px] font-mono uppercase tracking-widest text-text-muted/80">EP</span>
                        <span className="font-serif text-lg md:text-xl font-bold">{ep.num}</span>
                      </div>

                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-2.5">
                          <span className="text-[9px] uppercase tracking-widest text-gold font-bold bg-gold/10 px-2.5 py-0.5 rounded-full border border-gold/30">
                            {ep.status}
                          </span>
                          <span className="text-text-muted/40">•</span>
                          <span className="text-[10px] text-text-muted/80 font-mono tracking-widest uppercase">
                            Duración estimada: {ep.duration}
                          </span>
                        </div>

                        <h3 className="font-serif text-xl md:text-2xl mb-2 font-light tracking-wide text-white group-hover:text-gold transition-colors duration-300">
                          {ep.title}
                        </h3>

                        <p className="text-text-muted/90 font-light text-xs md:text-sm leading-relaxed mb-4">
                          {ep.description}
                        </p>

                        <div className="flex flex-wrap gap-2">
                          {ep.tags.map((tag) => (
                            <span
                              key={tag}
                              className="text-[9px] font-mono text-gold/70 bg-black/40 px-2 py-0.5 rounded border border-white/5"
                            >
                              #{tag}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="hidden lg:flex flex-col items-end shrink-0 pl-4 border-l border-gold/10">
                        <span className="text-[10px] font-mono text-gold/80 tracking-widest uppercase mb-1">Acceso Abierto</span>
                        <span className="text-[9px] text-text-muted/60">Audio en Alta Fidelidad</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </FadeIn>

          </div>
        </main>
        
        <Footer />
      </div>
    </PageTransition>
  );
}
